import React, { useState } from "react";
import Select, { Styles } from "react-select";
import { MenuPlacement, ValueType } from "react-select/src/types";

export type SelectOption = { value: any; label: string };

type DropdownProps = {
  options: { value: any; label: string }[];
  value: ValueType<SelectOption, false>; // ValueType<SelectOption, false> - false means isMulti = false
  onChange(selectedOption: ValueType<SelectOption, false>): void; // ValueType<SelectOption, false> - false means isMulti = false
  roundedRectangle?: boolean;
  small?: boolean;
  defaultMenuIsOpen?: boolean;
  customClassName?: string;
  customNoOptionsMessage?: string;
  onBlur?: () => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  portalMode?: boolean;
  menuPlacement?: MenuPlacement;
  refs?: { selectRef?: any; containerRef?: any };
  styles?:
    | Partial<
        Styles<
          {
            value: any;
            label: string;
          },
          false
        >
      >
    | undefined;
  menuPortalTarget?: HTMLElement | null | undefined;
  maxMenuHeight?: number | undefined;
  dropdownProps?: any;
  handleDropDownMenuOpen?: ((refs?: any) => void) | undefined;
};

type Props = DropdownProps;

const Dropdown: React.FC<Props> = ({
  options,
  value,
  onChange,
  roundedRectangle,
  small,
  defaultMenuIsOpen,
  customClassName,
  customNoOptionsMessage,
  onBlur,
  onMenuOpen,
  onMenuClose,
  placeholder,
  isSearchable,
  isClearable,
  portalMode,
  menuPlacement,
  refs,
  maxMenuHeight,
  menuPortalTarget,
  styles,
  dropdownProps,
  handleDropDownMenuOpen,
}) => {
  const [touched, setTouched] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  return (
    <Select
      className={`${
        roundedRectangle
          ? small
            ? "CustomSelect RoundedRect Small"
            : "CustomSelect RoundedRect"
          : small
          ? "CustomSelect Small"
          : "CustomSelect"
      }
          ${customClassName ? customClassName : ""}
          ${touched ? "Touched" : ""}
          `}
      defaultMenuIsOpen={defaultMenuIsOpen}
      classNamePrefix="CustomSelect"
      options={options}
      value={value}
      onChange={onChange}
      menuPlacement={menuPlacement ? menuPlacement : "auto"}
      noOptionsMessage={
        customNoOptionsMessage ? () => customNoOptionsMessage : undefined
      }
      onBlur={onBlur}
      placeholder={placeholder}
      isSearchable={isSearchable}
      menuIsOpen={menuOpen}
      onMenuOpen={() => {
        handleDropDownMenuOpen && handleDropDownMenuOpen(refs);
        onMenuOpen && onMenuOpen();
        dropdownProps?.onMenuOpen && dropdownProps?.onMenuOpen();
        setMenuOpen(true); // NOTE: This is needed to make click on mobile phones work
      }}
      onMenuClose={() => {
        onMenuClose && onMenuClose();
        setMenuOpen(false); // NOTE: This is needed to make click on mobile phones work
      }}
      isClearable={isClearable}
      styles={
        portalMode
          ? {
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              option: (base, { isSelected, isDisabled, isFocused }) => ({
                backgroundColor: isSelected
                  ? "#ff5a00"
                  : isFocused
                  ? "#ffe6d9"
                  : "#f5f5f5",
                color: isSelected ? "#ffffff" : "#58595b",
                fontSize: "12px",
                padding: "10px",
                cursor: isDisabled ? "not-allowed" : "pointer",
              }),
              menuList: base => ({
                ...base,
                backgroundColor: "#f5f5f5",
                borderRadius: "3px",
                margin: "5px 0",
              }),
              menu: base => ({
                ...base,
                backgroundColor: "#f5f5f5",
              }),
            }
          : undefined
      }
      menuPortalTarget={portalMode ? document.body : undefined}
      onFocus={() => setTouched(true)}
      menuShouldScrollIntoView
      ref={refs?.selectRef}
      maxMenuHeight={maxMenuHeight ?? dropdownProps?.maxMenuHeight}
      {...dropdownProps}
    />
  );
};

export { Dropdown };
